import React, { FC, useState, useEffect } from 'react';
import { differenceInHours, differenceInDays } from 'date-fns'
import { useWebsite } from '../../../hooks/useWebsite';
import { useDateRange } from '../../../hooks/useDateRange';

import {
  fetchTrafficEvolution,
  fetchTrafficRepartition,
  ITrafficDataPoint,
  ITrafficDayData,
  IDatePad
} from '../../../api';
import EvolutionChart from '../charts/EvolutionChart';
import TrafficHeatmap from '../charts/TrafficHeatmap';

interface CardProps {
  height: number;
}

/**
 * Hook to compute dat pad
 */
const computePad = (from: Date, to: Date): string => {
  const hoursDiff = differenceInHours(to, from);
  const daysDiff = differenceInDays(to, from);

  if (hoursDiff <= 48) return 'hour';
  if (daysDiff <= 60) return 'day';
  return 'month';
}

/**
 * Traffic chart card
 */
export const TrafficEvolutionCard: FC<CardProps> = ({ height }) => {
  const { websiteId } = useWebsite();
  const { from, to, timezone } = useDateRange();
  const [data, setData] = useState<Array<ITrafficDataPoint>>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const pad = computePad(from, to) as IDatePad;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      setData(await fetchTrafficEvolution(websiteId, {from, to, timezone, pad }) || []);
    } catch (e: unknown) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => { fetchData() }, [from, to, websiteId]);

  return (
    <div className="card my-5">
      <div className="card-header">
        <div className="card-title">
          Traffic Evolution
        </div>
      </div>
      <div className="card-body">
        {isLoading || data.length === 0 ? (
          <div
            className="align-items-center d-grid text-center"
            style={{minHeight: `${height}px`}}
          >
            {isLoading ? (
              <span>
                <i className='spinner-border fs-5x'></i>
              </span>
          ) : (
            <span>No data</span>
          )}
          </div>
        ) : (
          <EvolutionChart from={from} to={to} height={500} data={data} pad={pad} />
        )}
      </div>
    </div>
  );
}

/**
 * Traffic Heatmap card
 */
export const TrafficRepartitionCard: FC<CardProps> = ({ height }) => {
  const { websiteId } = useWebsite();
  const { from, to, timezone } = useDateRange();
  const [data, setData] = useState<Array<ITrafficDayData>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await fetchTrafficRepartition(websiteId, { from, to, timezone} ).then((data) => { void(data && setData(data)) });
    } catch (e: unknown) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => { fetchData() }, [from, to]);

  return (
    <div className="card my-5">
      <div className="card-header">
        <div className="card-title">Traffic</div>
      </div>
      <div className="card-body">
        {isLoading || data.length === 0 ? (
          <div
            className="align-items-center d-grid text-center"
            style={{minHeight: `${height}px`}}
          >
            <span>
              <i className='spinner-border fs-5x'></i>
            </span>
          </div>
        ) : (
          <TrafficHeatmap data={data} height={height} />
        )}
      </div>
    </div>
  );
}

